@switch (mode()) {
  @case ('querying') {
    <form
      class="flex flex-col gap-3"
      data-testid="openalex-form-querying"
      (ngSubmit)="loadPreview()"
    >
      <div class="flex flex-col gap-1">
        <label class="input-label" for="openalex-query">Search query</label>
        <input
          id="openalex-query"
          type="text"
          class="input-field"
          placeholder="e.g. vector search recommender systems"
          [value]="query()"
          (input)="setQuery($any($event.target).value)"
          required
          data-testid="openalex-query-input"
        />
      </div>

      <!-- Work type filter -->
      <div class="flex flex-col gap-1">
        <span class="input-label"
          >Work types <span class="text-gray-400 font-normal">(optional)</span></span
        >
        <div class="flex flex-wrap gap-2" data-testid="work-type-options">
          @for (opt of workTypeOptions; track opt.value) {
            <label class="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                class="rounded border-slate-300 text-blue-600"
                [value]="opt.value"
                [checked]="workTypes().includes(opt.value)"
                (change)="toggleWorkType(opt.value)"
                [attr.data-testid]="'work-type-' + opt.value"
              />
              <span class="text-sm text-slate-700">{{ opt.label }}</span>
            </label>
          }
        </div>
      </div>

      <!-- Source type filter -->
      <div class="flex flex-col gap-1">
        <span class="input-label"
          >Venue types <span class="text-gray-400 font-normal">(optional)</span></span
        >
        <div class="flex flex-wrap gap-2" data-testid="source-type-options">
          @for (opt of sourceTypeOptions; track opt.value) {
            <label class="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                class="rounded border-slate-300 text-blue-600"
                [value]="opt.value"
                [checked]="sourceTypes().includes(opt.value)"
                (change)="toggleSourceType(opt.value)"
                [attr.data-testid]="'source-type-' + opt.value"
              />
              <span class="text-sm text-slate-700">{{ opt.label }}</span>
            </label>
          }
        </div>
      </div>

      <!-- Year range filter -->
      <div class="flex flex-col gap-1">
        <span class="input-label"
          >Publication year <span class="text-gray-400 font-normal">(optional)</span></span
        >
        <div class="flex gap-2 items-center">
          <input
            type="number"
            class="input-field w-24"
            placeholder="From"
            min="1900"
            [max]="yearTo() ?? 2026"
            [value]="yearFrom() ?? ''"
            (input)="setYearFrom($any($event.target).value)"
            data-testid="year-from-input"
          />
          <span class="text-sm text-slate-400">–</span>
          <input
            type="number"
            class="input-field w-24"
            placeholder="To"
            [min]="yearFrom() ?? 1900"
            max="2026"
            [value]="yearTo() ?? ''"
            (input)="setYearTo($any($event.target).value)"
            data-testid="year-to-input"
          />
        </div>
      </div>

      <!-- Language filter -->
      <div class="flex flex-col gap-1">
        <span class="input-label"
          >Language <span class="text-gray-400 font-normal">(optional)</span></span
        >
        <div class="flex flex-wrap gap-2" data-testid="language-options">
          @for (opt of languageOptions; track opt.value) {
            <label class="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                class="rounded border-slate-300 text-blue-600"
                [value]="opt.value"
                [checked]="language().includes(opt.value)"
                (change)="toggleLanguage(opt.value)"
                [attr.data-testid]="'language-' + opt.value"
              />
              <span class="text-sm text-slate-700">{{ opt.label }}</span>
            </label>
          }
        </div>
        @if (customLanguages().length > 0) {
          <div class="flex flex-wrap gap-1 mt-1">
            @for (code of customLanguages(); track code) {
              <span
                class="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700 border border-blue-200"
              >
                {{ code }}
                <button
                  type="button"
                  class="ml-0.5 text-blue-400 hover:text-blue-700"
                  (click)="removeLanguage(code)"
                  [attr.data-testid]="'remove-language-' + code"
                >
                  ×
                </button>
              </span>
            }
          </div>
        }
        <div class="flex gap-1 items-center mt-1">
          <input
            type="text"
            class="input-field w-40 text-xs"
            placeholder="e.g. hu, nl, sv…"
            maxlength="40"
            [value]="customLanguageInput()"
            (input)="customLanguageInput.set($any($event.target).value)"
            (keydown.enter)="addCustomLanguage(customLanguageInput()); $event.preventDefault()"
            data-testid="custom-language-input"
          />
          <button
            type="button"
            class="btn-small-secondary text-xs"
            (click)="addCustomLanguage(customLanguageInput())"
            data-testid="add-custom-language"
          >
            Add
          </button>
        </div>
      </div>

      <!-- Show all types toggle -->
      <button
        type="button"
        class="self-start text-xs text-blue-600 hover:underline"
        (click)="toggleShowAllTypes()"
        data-testid="toggle-show-all-types"
      >
        {{ showAllTypes() ? 'Show fewer types' : 'Show all types' }}
      </button>

      @if (previewError(); as msg) {
        <p class="text-sm text-red-700" data-testid="openalex-preview-error">{{ msg }}</p>
      }

      <button
        type="submit"
        class="btn-primary w-full sm:w-auto sm:self-end"
        [disabled]="!isQueryValid() || previewLoading()"
        data-testid="openalex-preview-button"
      >
        {{ previewLoading() ? 'Loading preview…' : 'Preview' }}
      </button>
    </form>
  }
  @case ('previewing') {
    <div data-testid="openalex-form-previewing">
      @if (preview() !== null) {
        <app-openalex-preview
          [preview]="preview()"
          [query]="query()"
          [workTypes]="workTypes()"
          [sourceTypes]="sourceTypes()"
          [yearFrom]="yearFrom()"
          [yearTo]="yearTo()"
          [language]="language()"
          [submitting]="submitting()"
          (confirm)="confirmIngest()"
          (back)="backToQuery()"
        ></app-openalex-preview>
      }
      @if (submitError(); as msg) {
        <p class="mt-2 text-sm text-red-700" data-testid="openalex-submit-error">{{ msg }}</p>
      }
      @if (submitting()) {
        <p class="mt-2 text-sm text-gray-500" data-testid="openalex-submitting">Starting import…</p>
      }
    </div>
  }
}
